import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Shield, 
  Smartphone, 
  Wifi, 
  Clock, 
  Lock, 
  Unlock,
  Bell,
  BarChart3,
  CheckCircle2,
  Loader2,
  ChevronRight,
  ChevronDown,
  Zap,
  Users,
  Euro,
  Car,
  Key,
  Share2,
  Settings,
  Search,
  Calendar,
  MapPin,
  CreditCard,
  HelpCircle,
  Wrench,
  Building2,
  Plug,
  MessageCircle
} from "lucide-react";

import { API } from "../utils/api";

// Demo steps for OWNER (propietario)
const ownerSteps = [
  {
    id: 0,
    title: "Instala el dispositivo",
    description: "Conecta Plazafy Gate al motor de tu puerta en menos de 10 minutos",
    icon: Settings,
    screen: "install"
  },
  {
    id: 1,
    title: "Publica tu plaza",
    description: "Añade tu plaza a Plazafy y marca que tiene Gate instalado",
    icon: Car,
    screen: "publish"
  },
  {
    id: 2,
    title: "Recibe reservas",
    description: "Los inquilinos reservan y reciben acceso automático",
    icon: Key,
    screen: "booking"
  },
  {
    id: 3,
    title: "Control total",
    description: "Gestiona accesos, ve historial y abre remotamente cuando quieras",
    icon: Smartphone,
    screen: "control"
  }
];

// Demo steps for RENTER (inquilino)
const renterSteps = [
  {
    id: 0,
    title: "Busca una plaza con Gate",
    description: "Filtra por plazas con Plazafy Gate para acceso sin complicaciones",
    icon: Search,
    screen: "search"
  },
  {
    id: 1,
    title: "Reserva tu plaza",
    description: "Elige las fechas y confirma tu reserva de forma segura",
    icon: Calendar,
    screen: "reserve"
  },
  {
    id: 2,
    title: "Recibe acceso automático",
    description: "Al comenzar tu reserva, se activa tu acceso al garaje",
    icon: Key,
    screen: "access"
  },
  {
    id: 3,
    title: "Abre desde tu móvil",
    description: "Entra y sal del garaje con un solo toque durante tu reserva",
    icon: Unlock,
    screen: "open"
  }
];

export default function PlazafyGate() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [demoRole, setDemoRole] = useState("owner"); // "owner" or "renter"
  const [activeFaq, setActiveFaq] = useState(null);
  const [deviceState, setDeviceState] = useState('idle'); // 'idle', 'connecting', 'opening', 'open', 'closing'
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    num_spots: 1,
    city: "",
    comments: ""
  });

  // Get current steps based on role
  const currentSteps = demoRole === "owner" ? ownerSteps : renterSteps;

  // Auto-advance demo steps
  useEffect(() => {
    if (!isAnimating) return;
    
    const interval = setInterval(() => {
      setDemoStep((prev) => (prev + 1) % currentSteps.length);
    }, 3500);
    
    return () => clearInterval(interval);
  }, [isAnimating, currentSteps.length]);

  // Reset step when changing role
  const handleRoleChange = (role) => {
    setDemoRole(role);
    setDemoStep(0);
    setIsAnimating(true);
  };

  // Simulate device action
  const simulateDeviceAction = (action) => {
    setDeviceState('connecting');
    
    setTimeout(() => {
      setDeviceState('opening');
      
      setTimeout(() => {
        setDeviceState('open');
        
        setTimeout(() => {
          setDeviceState('closing');
          
          setTimeout(() => {
            setDeviceState('idle');
          }, 2000);
        }, 3000);
      }, 1500);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error("Por favor, introduce tu email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/gate/waitlist`, formData);
      setSubmitted(true);
      toast.success("¡Te has unido a la lista de espera!");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al registrarte";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Smartphone,
      title: "Control desde el móvil",
      description: "Abre y cierra tu garaje desde cualquier lugar con la app"
    },
    {
      icon: Unlock,
      title: "Acceso automático",
      description: "Tus inquilinos acceden sin llaves durante su reserva"
    },
    {
      icon: Clock,
      title: "Historial de accesos",
      description: "Registro completo de todas las entradas y salidas"
    },
    {
      icon: Bell,
      title: "Notificaciones",
      description: "Alertas en tiempo real cuando alguien accede"
    },
    {
      icon: Wifi,
      title: "Conectividad 4G",
      description: "Funciona sin WiFi, conexión independiente"
    },
    {
      icon: BarChart3,
      title: "Analytics",
      description: "Estadísticas de uso y ocupación de tu plaza"
    }
  ];

  const benefits = [
    {
      icon: Euro,
      title: "Más ingresos",
      description: "Las plazas con Gate tienen un 40% más de reservas"
    },
    {
      icon: Shield,
      title: "Mayor seguridad",
      description: "Control total sobre quién accede y cuándo"
    },
    {
      icon: Users,
      title: "Sin complicaciones",
      description: "Olvídate de entregar llaves o mandos"
    },
    {
      icon: Zap,
      title: "Ingresos pasivos",
      description: "Tu plaza genera dinero mientras tú no estás"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0f0d15]">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/10 via-transparent to-transparent" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 border border-violet-500/30 rounded-full text-violet-400 text-sm mb-8">
              <Zap className="w-4 h-4" />
              <span>Próximamente disponible</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Plazafy <span className="text-violet-400">Gate</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-4">
              Control de acceso inteligente para tu plaza de garaje
            </p>
            <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
              Olvídate de llaves y mandos. Gestiona el acceso a tu garaje desde el móvil 
              y permite que tus inquilinos entren automáticamente durante su reserva.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#waitlist">
                <Button className="h-14 px-8 bg-violet-500 hover:bg-violet-600 text-white text-lg rounded-xl">
                  <Bell className="w-5 h-5 mr-2" />
                  Unirme a la lista de espera
                </Button>
              </a>
              <Link to="/explore">
                <Button variant="outline" className="h-14 px-8 border-white/20 text-white hover:bg-white/5 text-lg rounded-xl">
                  Explorar plazas
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Device Mockup */}
          <div className="mt-20 relative">
            <div className="max-w-md mx-auto">
              <div className="bg-gradient-to-b from-[#231f30] to-[#1a1625] rounded-3xl p-8 border border-white/10 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center">
                      <Lock className="w-6 h-6 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Mi Garaje</p>
                      <p className="text-gray-400 text-sm">Calle Gran Vía 28</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm rounded-full border border-emerald-500/30">
                    Online
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#15121e] rounded-xl p-4 border border-white/5">
                    <p className="text-gray-400 text-sm mb-1">Estado</p>
                    <p className="text-white font-semibold flex items-center gap-2">
                      <Lock className="w-4 h-4 text-emerald-400" />
                      Cerrado
                    </p>
                  </div>
                  <div className="bg-[#15121e] rounded-xl p-4 border border-white/5">
                    <p className="text-gray-400 text-sm mb-1">Último acceso</p>
                    <p className="text-white font-semibold">Hace 2h</p>
                  </div>
                </div>

                <button className="w-full py-4 bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25">
                  <Unlock className="w-5 h-5" />
                  Abrir puerta
                </button>
                
                <p className="text-center text-gray-500 text-sm mt-4">
                  Desliza para abrir temporalmente
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section className="py-24 bg-gradient-to-b from-[#0f0d15] to-[#1a1625]/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              ¿Cómo funciona?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Descubre la experiencia según tu rol
            </p>
            
            {/* Role Toggle Tabs */}
            <div className="inline-flex items-center p-1.5 bg-[#231f30] rounded-2xl border border-white/10">
              <button
                onClick={() => handleRoleChange("owner")}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                  demoRole === "owner"
                    ? "bg-violet-500 text-white shadow-lg shadow-violet-500/25"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Car className="w-5 h-5" />
                <span>Soy propietario</span>
              </button>
              <button
                onClick={() => handleRoleChange("renter")}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                  demoRole === "renter"
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <Users className="w-5 h-5" />
                <span>Soy inquilino</span>
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Steps */}
            <div className="order-2 lg:order-1">
              <div className="space-y-4">
                {currentSteps.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => {
                      setDemoStep(index);
                      setIsAnimating(false);
                    }}
                    className={`w-full text-left p-5 rounded-2xl transition-all duration-500 ${
                      demoStep === index
                        ? demoRole === "owner"
                          ? "bg-gradient-to-r from-violet-500/20 to-violet-500/5 border border-violet-500/40 scale-[1.02]"
                          : "bg-gradient-to-r from-emerald-500/20 to-emerald-500/5 border border-emerald-500/40 scale-[1.02]"
                        : "bg-[#231f30]/50 border border-white/5 hover:border-white/10"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                        demoStep === index 
                          ? demoRole === "owner"
                            ? "bg-violet-500 text-white shadow-lg shadow-violet-500/30"
                            : "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30"
                          : "bg-white/5 text-gray-400"
                      }`}>
                        <step.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium ${
                            demoStep === index 
                              ? demoRole === "owner" ? "text-violet-400" : "text-emerald-400"
                              : "text-gray-500"
                          }`}>
                            Paso {index + 1}
                          </span>
                          {demoStep === index && (
                            <span className={`w-2 h-2 rounded-full animate-pulse ${
                              demoRole === "owner" ? "bg-violet-500" : "bg-emerald-500"
                            }`} />
                          )}
                        </div>
                        <h3 className={`text-lg font-semibold mb-1 transition-colors ${
                          demoStep === index ? "text-white" : "text-gray-300"
                        }`}>
                          {step.title}
                        </h3>
                        <p className={`text-sm transition-colors ${
                          demoStep === index ? "text-gray-300" : "text-gray-500"
                        }`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Progress bar */}
              <div className="mt-6 flex items-center gap-3">
                <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ease-out ${
                      demoRole === "owner" 
                        ? "bg-gradient-to-r from-violet-500 to-violet-400"
                        : "bg-gradient-to-r from-emerald-500 to-emerald-400"
                    }`}
                    style={{ width: `${((demoStep + 1) / currentSteps.length) * 100}%` }}
                  />
                </div>
                <button
                  onClick={() => setIsAnimating(!isAnimating)}
                  className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1"
                >
                  {isAnimating ? (
                    <>
                      <span className={`w-2 h-2 rounded-full animate-pulse ${
                        demoRole === "owner" ? "bg-violet-500" : "bg-emerald-500"
                      }`} />
                      Auto
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-gray-500 rounded-full" />
                      Manual
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Phone Mockup with animated screens */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                {/* Glow effect */}
                <div className={`absolute inset-0 rounded-[3rem] blur-3xl scale-110 animate-pulse transition-colors duration-500 ${
                  demoRole === "owner" ? "bg-violet-500/20" : "bg-emerald-500/20"
                }`} />
                
                {/* Phone frame */}
                <div className="relative w-[280px] sm:w-[320px] h-[580px] sm:h-[640px] bg-gradient-to-b from-[#2a2535] to-[#1a1625] rounded-[3rem] p-3 border border-white/10 shadow-2xl">
                  {/* Phone notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-[#0f0d15] rounded-b-2xl z-20" />
                  
                  {/* Screen */}
                  <div className="w-full h-full bg-[#0f0d15] rounded-[2.5rem] overflow-hidden relative">
                    {/* Status bar */}
                    <div className="absolute top-0 left-0 right-0 h-12 bg-[#0f0d15] z-10 flex items-end justify-between px-6 pb-1">
                      <span className="text-white text-xs font-medium">9:41</span>
                      <div className="flex items-center gap-1">
                        <Wifi className="w-3.5 h-3.5 text-white" />
                        <div className="w-6 h-2.5 bg-white rounded-sm" />
                      </div>
                    </div>
                    
                    {/* OWNER SCREENS */}
                    {demoRole === "owner" && (
                      <div className="pt-14 px-4 h-full">
                        {/* Screen 0: Install */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 0 ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-full"
                        }`}>
                          <div className="text-center pt-8">
                            <div className="w-20 h-20 mx-auto bg-violet-500/20 rounded-2xl flex items-center justify-center mb-6 animate-bounce">
                              <Settings className="w-10 h-10 text-violet-400" />
                            </div>
                            <h4 className="text-white font-semibold text-lg mb-2">Configurando...</h4>
                            <p className="text-gray-400 text-sm mb-8">Conectando dispositivo</p>
                            
                            <div className="space-y-3">
                              <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                <span className="text-sm text-gray-300">Dispositivo detectado</span>
                              </div>
                              <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                <span className="text-sm text-gray-300">WiFi conectado</span>
                              </div>
                              <div className="flex items-center gap-3 p-3 bg-violet-500/10 rounded-lg border border-violet-500/20 animate-pulse">
                                <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                                <span className="text-sm text-gray-300">Sincronizando...</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Screen 1: Publish */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 1 ? "opacity-100 translate-x-0" : demoStep > 1 ? "opacity-0 -translate-x-full" : "opacity-0 translate-x-full"
                        }`}>
                          <div className="pt-4">
                            <div className="bg-[#231f30] rounded-xl p-4 mb-4 border border-white/10">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-16 h-16 bg-gradient-to-br from-violet-500/30 to-emerald-500/20 rounded-lg flex items-center justify-center">
                                  <Car className="w-8 h-8 text-violet-400" />
                                </div>
                                <div>
                                  <h4 className="text-white font-medium">Mi Garaje</h4>
                                  <p className="text-gray-400 text-xs">Calle Gran Vía 28</p>
                                </div>
                              </div>
                              <div className="flex items-center justify-between p-3 bg-violet-500/10 rounded-lg border border-violet-500/30">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-5 h-5 text-violet-400" />
                                  <span className="text-sm text-white">Plazafy Gate</span>
                                </div>
                                <div className="w-10 h-6 bg-violet-500 rounded-full flex items-center justify-end px-1">
                                  <div className="w-4 h-4 bg-white rounded-full shadow-md" />
                                </div>
                              </div>
                            </div>
                            <button className="w-full py-3 bg-violet-500 text-white rounded-xl font-medium flex items-center justify-center gap-2">
                              <CheckCircle2 className="w-5 h-5" />
                              Publicar plaza
                            </button>
                          </div>
                        </div>

                        {/* Screen 2: Booking received */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 2 ? "opacity-100 translate-x-0" : demoStep > 2 ? "opacity-0 -translate-x-full" : "opacity-0 translate-x-full"
                        }`}>
                          <div className="pt-4">
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <Bell className="w-5 h-5 text-emerald-400" />
                                <span className="text-emerald-400 font-medium text-sm">Nueva reserva</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <p className="text-white font-medium">Carlos García</p>
                                  <p className="text-gray-400 text-xs">15 Dic - 20 Dic (5 días)</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-[#231f30] rounded-xl p-4 border border-white/10">
                              <p className="text-gray-400 text-xs mb-2">Acceso automático</p>
                              <div className="flex items-center gap-2 text-white">
                                <Key className="w-4 h-4 text-violet-400" />
                                <span className="text-sm">El inquilino podrá acceder durante su reserva</span>
                              </div>
                            </div>
                            
                            <div className="mt-4 text-center">
                              <p className="text-2xl font-bold text-white">+75€</p>
                              <p className="text-emerald-400 text-sm">Ingreso confirmado</p>
                            </div>
                          </div>
                        </div>

                        {/* Screen 3: Control */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 3 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full"
                        }`}>
                          <div className="pt-4">
                            <div className="flex items-center justify-between mb-6">
                              <div>
                                <h4 className="text-white font-semibold">Mi Garaje</h4>
                                <p className="text-gray-400 text-xs flex items-center gap-1">
                                  <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                                  Online
                                </p>
                              </div>
                              <div className="p-2 bg-white/5 rounded-lg">
                                <BarChart3 className="w-5 h-5 text-gray-400" />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              <div className="bg-[#231f30] rounded-xl p-3 border border-white/10">
                                <Lock className="w-5 h-5 text-emerald-400 mb-2" />
                                <p className="text-white font-medium text-sm">Cerrado</p>
                                <p className="text-gray-500 text-xs">Estado actual</p>
                              </div>
                              <div className="bg-[#231f30] rounded-xl p-3 border border-white/10">
                                <Clock className="w-5 h-5 text-violet-400 mb-2" />
                                <p className="text-white font-medium text-sm">Hace 2h</p>
                                <p className="text-gray-500 text-xs">Último acceso</p>
                              </div>
                            </div>
                            
                            <button className="w-full py-4 bg-gradient-to-r from-violet-500 to-violet-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 shadow-lg shadow-violet-500/25">
                              <Unlock className="w-5 h-5" />
                              Abrir puerta
                            </button>
                            
                            <div className="mt-4 space-y-2">
                              <div className="flex items-center justify-between text-sm p-2 bg-white/5 rounded-lg">
                                <span className="text-gray-400">Carlos G.</span>
                                <span className="text-gray-500">Hoy 14:32</span>
                              </div>
                              <div className="flex items-center justify-between text-sm p-2 bg-white/5 rounded-lg">
                                <span className="text-gray-400">Tú</span>
                                <span className="text-gray-500">Ayer 09:15</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* RENTER SCREENS */}
                    {demoRole === "renter" && (
                      <div className="pt-14 px-4 h-full">
                        {/* Screen 0: Search */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 0 ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-full"
                        }`}>
                          <div className="pt-4">
                            <div className="bg-[#231f30] rounded-xl p-3 mb-4 border border-white/10 flex items-center gap-2">
                              <Search className="w-5 h-5 text-gray-400" />
                              <span className="text-gray-400 text-sm">Madrid centro...</span>
                            </div>
                            
                            <div className="flex items-center gap-2 mb-4">
                              <span className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30 flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Solo con Gate
                              </span>
                              <span className="px-3 py-1.5 bg-white/5 text-gray-400 text-xs rounded-full">Cubierta</span>
                            </div>
                            
                            <div className="space-y-3">
                              <div className="bg-[#231f30] rounded-xl p-3 border border-emerald-500/30">
                                <div className="flex items-start gap-3">
                                  <div className="w-16 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg flex items-center justify-center">
                                    <Car className="w-6 h-6 text-gray-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-white text-sm font-medium">Plaza Gran Vía</h4>
                                      <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded flex items-center gap-0.5">
                                        <Shield className="w-2.5 h-2.5" />
                                        Gate
                                      </span>
                                    </div>
                                    <p className="text-gray-500 text-xs">2.50€/hora • 4.8★</p>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-[#231f30] rounded-xl p-3 border border-white/10 opacity-60">
                                <div className="flex items-start gap-3">
                                  <div className="w-16 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg flex items-center justify-center">
                                    <Car className="w-6 h-6 text-gray-500" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-white text-sm font-medium">Parking Centro</h4>
                                    <p className="text-gray-500 text-xs">3.00€/hora • 4.5★</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Screen 1: Reserve */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 1 ? "opacity-100 translate-x-0" : demoStep > 1 ? "opacity-0 -translate-x-full" : "opacity-0 translate-x-full"
                        }`}>
                          <div className="pt-4">
                            <div className="flex items-center gap-2 mb-4">
                              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Gate
                              </span>
                              <h4 className="text-white font-medium">Plaza Gran Vía</h4>
                            </div>
                            
                            <div className="bg-[#231f30] rounded-xl p-4 mb-4 border border-white/10">
                              <div className="flex items-center gap-2 mb-3">
                                <Calendar className="w-5 h-5 text-emerald-400" />
                                <span className="text-white text-sm font-medium">Fechas seleccionadas</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-center">
                                <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                  <p className="text-gray-400 text-xs">Entrada</p>
                                  <p className="text-white font-medium">15 Dic</p>
                                </div>
                                <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                  <p className="text-gray-400 text-xs">Salida</p>
                                  <p className="text-white font-medium">20 Dic</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between mb-4 p-3 bg-white/5 rounded-lg">
                              <span className="text-gray-400">Total (5 días)</span>
                              <span className="text-white font-bold">75€</span>
                            </div>
                            
                            <button className="w-full py-3 bg-emerald-500 text-white rounded-xl font-medium flex items-center justify-center gap-2">
                              <CreditCard className="w-5 h-5" />
                              Confirmar reserva
                            </button>
                          </div>
                        </div>

                        {/* Screen 2: Access granted */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 2 ? "opacity-100 translate-x-0" : demoStep > 2 ? "opacity-0 -translate-x-full" : "opacity-0 translate-x-full"
                        }`}>
                          <div className="pt-8 text-center">
                            <div className="w-20 h-20 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-6 animate-pulse">
                              <Key className="w-10 h-10 text-emerald-400" />
                            </div>
                            <h4 className="text-white font-semibold text-lg mb-2">¡Acceso activado!</h4>
                            <p className="text-gray-400 text-sm mb-6">Tu reserva ha comenzado</p>
                            
                            <div className="bg-[#231f30] rounded-xl p-4 border border-emerald-500/30 text-left">
                              <div className="flex items-center gap-2 mb-3">
                                <MapPin className="w-5 h-5 text-emerald-400" />
                                <span className="text-white font-medium">Plaza Gran Vía</span>
                              </div>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Acceso válido</span>
                                  <span className="text-emerald-400">15-20 Dic</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-400">Estado</span>
                                  <span className="text-emerald-400 flex items-center gap-1">
                                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                                    Activo
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Screen 3: Open gate */}
                        <div className={`absolute inset-0 pt-14 px-4 transition-all duration-700 ${
                          demoStep === 3 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full"
                        }`}>
                          <div className="pt-4">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h4 className="text-white font-semibold">Plaza Gran Vía</h4>
                                <p className="text-emerald-400 text-xs flex items-center gap-1">
                                  <span className="w-2 h-2 bg-emerald-500 rounded-full" />
                                  Acceso activo
                                </p>
                              </div>
                              <span className="text-gray-400 text-xs">Hasta 20 Dic</span>
                            </div>
                            
                            <div className="bg-[#231f30] rounded-xl p-4 mb-6 border border-white/10 text-center">
                              <Lock className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                              <p className="text-white font-medium">Puerta cerrada</p>
                              <p className="text-gray-500 text-xs">Toca para abrir</p>
                            </div>
                            
                            <button className="w-full py-5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-2xl font-semibold flex items-center justify-center gap-3 shadow-lg shadow-emerald-500/25 active:scale-95 transition-transform text-lg">
                              <Unlock className="w-6 h-6" />
                              Abrir garaje
                            </button>
                            
                            <p className="text-center text-gray-500 text-xs mt-4">
                              La puerta se cerrará automáticamente en 30 segundos
                            </p>
                            
                            <div className="mt-6 p-3 bg-white/5 rounded-lg">
                              <p className="text-gray-400 text-xs mb-1">Último acceso</p>
                              <p className="text-white text-sm">Hoy a las 09:15</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Home indicator */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Device Section */}
      <section className="py-24 bg-[#0f0d15]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-violet-500/20 text-violet-400 text-sm font-medium rounded-full mb-4">
              El dispositivo
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Conoce Plazafy Gate
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Un dispositivo compacto y elegante que se conecta a tu motor de garaje en minutos
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Device Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-violet-500/10 rounded-3xl blur-3xl" />
              <div className="relative bg-gradient-to-b from-[#231f30] to-[#1a1625] rounded-3xl p-8 border border-white/10">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/253ffc3a-02f7-4a11-8081-5a9bf4166219/images/e64595ab79f016b7cebd949d0ac45c5345b8c395bd1902bfc31b08bbff9b2f65.png"
                  alt="Plazafy Gate Device"
                  className="w-full max-w-sm mx-auto"
                />
              </div>
            </div>

            {/* Device Specs */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Especificaciones técnicas
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-[#231f30] rounded-xl border border-white/5">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Wifi className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Conectividad WiFi 2.4GHz</h4>
                    <p className="text-gray-400 text-sm">Conexión estable y segura a tu red doméstica</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#231f30] rounded-xl border border-white/5">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Alimentación 12-24V DC</h4>
                    <p className="text-gray-400 text-sm">Compatible con la mayoría de motores de garaje</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#231f30] rounded-xl border border-white/5">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Encriptación AES-256</h4>
                    <p className="text-gray-400 text-sm">Comunicación cifrada de extremo a extremo</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-[#231f30] rounded-xl border border-white/5">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Instalación en 10 minutos</h4>
                    <p className="text-gray-400 text-sm">Sin herramientas especiales ni técnicos</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-gradient-to-r from-violet-500/10 to-emerald-500/10 rounded-xl border border-violet-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-violet-500 rounded-xl flex items-center justify-center">
                    <Euro className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">Precio estimado: 89€</p>
                    <p className="text-gray-400 text-sm">Envío e instalación guiada incluidos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Device Demo - How it works */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 text-sm font-medium rounded-full mb-4">
                Demo en vivo
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Así funciona el dispositivo
              </h3>
              <p className="text-gray-400 max-w-xl mx-auto">
                Simula las diferentes situaciones para ver cómo responde Plazafy Gate
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Device Visual Simulation */}
              <div className="relative flex justify-center">
                <div className="relative">
                  {/* Glow effect based on state */}
                  <div className={`absolute inset-0 rounded-3xl blur-3xl scale-110 transition-colors duration-1000 ${
                    deviceState === 'idle' ? 'bg-violet-500/10' :
                    deviceState === 'connecting' ? 'bg-yellow-500/20 animate-pulse' :
                    deviceState === 'opening' ? 'bg-emerald-500/30 animate-pulse' :
                    deviceState === 'open' ? 'bg-emerald-500/20' :
                    deviceState === 'closing' ? 'bg-orange-500/20 animate-pulse' :
                    'bg-violet-500/10'
                  }`} />
                  
                  {/* Device body */}
                  <div className="relative w-64 h-80 bg-gradient-to-b from-[#2a2535] to-[#1a1625] rounded-3xl border border-white/10 shadow-2xl p-6 flex flex-col">
                    {/* Top section with LED */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-violet-400" />
                        <span className="text-white text-sm font-medium">Plazafy Gate</span>
                      </div>
                      {/* LED Indicator */}
                      <div className={`w-4 h-4 rounded-full transition-all duration-500 ${
                        deviceState === 'idle' ? 'bg-violet-500 shadow-lg shadow-violet-500/50' :
                        deviceState === 'connecting' ? 'bg-yellow-500 animate-pulse shadow-lg shadow-yellow-500/50' :
                        deviceState === 'opening' ? 'bg-emerald-500 animate-ping shadow-lg shadow-emerald-500/50' :
                        deviceState === 'open' ? 'bg-emerald-500 shadow-lg shadow-emerald-500/50' :
                        deviceState === 'closing' ? 'bg-orange-500 animate-pulse shadow-lg shadow-orange-500/50' :
                        'bg-violet-500 shadow-lg shadow-violet-500/50'
                      }`} />
                    </div>

                    {/* Status screen */}
                    <div className="flex-1 bg-[#0f0d15] rounded-2xl p-4 border border-white/5">
                      {/* Status display */}
                      <div className="h-full flex flex-col items-center justify-center text-center">
                        {deviceState === 'idle' && (
                          <div className="animate-fadeIn">
                            <Lock className="w-12 h-12 text-violet-400 mx-auto mb-3" />
                            <p className="text-white font-medium">Cerrado</p>
                            <p className="text-gray-500 text-xs mt-1">En espera</p>
                          </div>
                        )}
                        {deviceState === 'connecting' && (
                          <div className="animate-fadeIn">
                            <Wifi className="w-12 h-12 text-yellow-400 mx-auto mb-3 animate-pulse" />
                            <p className="text-white font-medium">Conectando...</p>
                            <p className="text-yellow-400 text-xs mt-1">Verificando acceso</p>
                          </div>
                        )}
                        {deviceState === 'opening' && (
                          <div className="animate-fadeIn">
                            <Unlock className="w-12 h-12 text-emerald-400 mx-auto mb-3 animate-bounce" />
                            <p className="text-white font-medium">Abriendo...</p>
                            <p className="text-emerald-400 text-xs mt-1">Activando motor</p>
                          </div>
                        )}
                        {deviceState === 'open' && (
                          <div className="animate-fadeIn">
                            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                            <p className="text-white font-medium">Abierto</p>
                            <p className="text-emerald-400 text-xs mt-1">Acceso concedido</p>
                          </div>
                        )}
                        {deviceState === 'closing' && (
                          <div className="animate-fadeIn">
                            <Lock className="w-12 h-12 text-orange-400 mx-auto mb-3 animate-pulse" />
                            <p className="text-white font-medium">Cerrando...</p>
                            <p className="text-orange-400 text-xs mt-1">Auto-cierre</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bottom connection indicator */}
                    <div className="mt-4 flex items-center justify-center gap-4">
                      <div className="flex items-center gap-1">
                        <Wifi className={`w-4 h-4 ${deviceState !== 'idle' ? 'text-emerald-400' : 'text-gray-500'}`} />
                        <span className="text-xs text-gray-400">WiFi</span>
                      </div>
                      <div className="w-px h-4 bg-white/10" />
                      <div className="flex items-center gap-1">
                        <Zap className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs text-gray-400">12V</span>
                      </div>
                    </div>
                  </div>

                  {/* Connection cables visual */}
                  <div className="absolute -right-4 top-1/2 transform -translate-y-1/2">
                    <div className="flex flex-col gap-2">
                      <div className={`w-8 h-1 rounded-full transition-colors ${deviceState !== 'idle' ? 'bg-emerald-500' : 'bg-gray-600'}`} />
                      <div className={`w-8 h-1 rounded-full transition-colors ${deviceState !== 'idle' ? 'bg-violet-500' : 'bg-gray-600'}`} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Demo Controls */}
              <div className="space-y-6">
                <div className="bg-[#231f30] rounded-2xl p-6 border border-white/10">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-violet-400" />
                    Simular acciones
                  </h4>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => simulateDeviceAction('open')}
                      disabled={deviceState !== 'idle'}
                      className={`w-full p-4 rounded-xl font-medium transition-all flex items-center justify-between ${
                        deviceState === 'idle'
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-white/5 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Unlock className="w-5 h-5" />
                        Inquilino abre desde la app
                      </span>
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() => simulateDeviceAction('manual')}
                      disabled={deviceState !== 'idle'}
                      className={`w-full p-4 rounded-xl font-medium transition-all flex items-center justify-between ${
                        deviceState === 'idle'
                          ? 'bg-violet-500 hover:bg-violet-600 text-white'
                          : 'bg-white/5 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Key className="w-5 h-5" />
                        Propietario abre remotamente
                      </span>
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() => setDeviceState('idle')}
                      disabled={deviceState === 'idle'}
                      className={`w-full p-4 rounded-xl font-medium transition-all flex items-center justify-between ${
                        deviceState !== 'idle'
                          ? 'bg-white/10 hover:bg-white/20 text-white'
                          : 'bg-white/5 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Lock className="w-5 h-5" />
                        Reiniciar demo
                      </span>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Status explanation */}
                <div className="bg-[#231f30]/50 rounded-xl p-5 border border-white/5">
                  <h5 className="text-white font-medium mb-3">Estados del LED</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-violet-500" />
                      <span className="text-gray-400 text-sm">Listo / Cerrado</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <span className="text-gray-400 text-sm">Conectando</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-gray-400 text-sm">Abierto / OK</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500" />
                      <span className="text-gray-400 text-sm">Cerrando</span>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                {deviceState !== 'idle' && (
                  <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20 animate-fadeIn">
                    <p className="text-emerald-400 text-sm font-medium mb-2">Proceso en curso:</p>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${deviceState === 'connecting' ? 'bg-yellow-500 animate-pulse' : 'bg-emerald-500'}`} />
                      <span className={`text-sm ${deviceState === 'connecting' ? 'text-yellow-400' : 'text-gray-400'}`}>Verificando acceso</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`w-2 h-2 rounded-full ${deviceState === 'opening' ? 'bg-emerald-500 animate-pulse' : deviceState === 'open' || deviceState === 'closing' ? 'bg-emerald-500' : 'bg-gray-600'}`} />
                      <span className={`text-sm ${deviceState === 'opening' ? 'text-emerald-400' : deviceState === 'open' || deviceState === 'closing' ? 'text-gray-400' : 'text-gray-600'}`}>Activando motor</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`w-2 h-2 rounded-full ${deviceState === 'open' ? 'bg-emerald-500' : deviceState === 'closing' ? 'bg-orange-500 animate-pulse' : 'bg-gray-600'}`} />
                      <span className={`text-sm ${deviceState === 'open' ? 'text-emerald-400' : deviceState === 'closing' ? 'text-orange-400' : 'text-gray-600'}`}>
                        {deviceState === 'closing' ? 'Auto-cierre activo' : 'Puerta abierta'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Installation Preview */}
          <div className="mt-20">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-white mb-3">
                Instalación sencilla
              </h3>
              <p className="text-gray-400">
                Conecta el dispositivo al motor de tu garaje y configúralo desde la app
              </p>
            </div>
            
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/253ffc3a-02f7-4a11-8081-5a9bf4166219/images/fc6d5faaf2fb899828e0a46ada035abe4be8604acea6e28992d23c5c396a0e52.png"
                alt="Plazafy Gate Instalación"
                className="w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0d15] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="flex flex-wrap gap-4 justify-center">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span className="text-white text-sm">Compatible con todos los motores</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span className="text-white text-sm">Sin modificar tu instalación</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <span className="text-white text-sm">Soporte técnico incluido</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-[#1a1625]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Todo lo que necesitas
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Un dispositivo compacto con tecnología avanzada para el control total de tu garaje
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-[#231f30] rounded-2xl p-6 border border-white/5 hover:border-violet-500/30 transition-all group"
              >
                <div className="w-12 h-12 bg-violet-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-violet-500/30 transition-colors">
                  <feature.icon className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                ¿Por qué Plazafy Gate?
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Maximiza tus ingresos y simplifica la gestión de tu plaza con tecnología IoT de última generación.
              </p>
              
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-gray-400">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-violet-500/20 to-emerald-500/20 rounded-3xl p-1">
                <div className="bg-[#1a1625] rounded-3xl p-8">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-violet-500/20 rounded-2xl mb-4">
                      <Shield className="w-10 h-10 text-violet-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">Plazafy Gate</h3>
                    <p className="text-gray-400">Dispositivo IoT</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Instalación sencilla en 10 minutos</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Compatible con cualquier puerta</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Conexión 4G incluida</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Batería de respaldo</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-300">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span>Soporte técnico incluido</span>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <div className="flex items-end justify-center gap-1 mb-2">
                      <span className="text-4xl font-bold text-white">12€</span>
                      <span className="text-gray-400 mb-1">/mes</span>
                    </div>
                    <p className="text-center text-gray-500 text-sm">Precio estimado (puede variar)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive FAQ Section */}
      <section className="py-24 bg-[#0f0d15]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 text-sm font-medium rounded-full mb-4">
              Resuelve tus dudas
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Preguntas frecuentes
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Todo lo que necesitas saber sobre Plazafy Gate
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* FAQ List */}
            <div className="lg:col-span-2 space-y-3">
              {[
                { id: 1, icon: Wrench, question: "¿Necesito un electricista para instalarlo?", color: "violet" },
                { id: 2, icon: Building2, question: "¿Funciona con mi tipo de garaje?", color: "emerald" },
                { id: 3, icon: Wifi, question: "¿Qué pasa si se va la luz o el WiFi?", color: "orange" },
                { id: 4, icon: Shield, question: "¿Es seguro? ¿Pueden hackearme?", color: "blue" },
                { id: 5, icon: Euro, question: "¿Cuánto cuesta realmente?", color: "violet" },
                { id: 6, icon: MessageCircle, question: "¿Qué soporte ofrecéis?", color: "emerald" },
              ].map((faq) => (
                <button
                  key={faq.id}
                  onClick={() => setActiveFaq(activeFaq === faq.id ? null : faq.id)}
                  className={`w-full text-left p-4 rounded-xl transition-all duration-300 flex items-center gap-3 ${
                    activeFaq === faq.id
                      ? "bg-gradient-to-r from-violet-500/20 to-emerald-500/10 border border-violet-500/40 scale-[1.02]"
                      : "bg-[#231f30]/70 border border-white/5 hover:border-white/10"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                    activeFaq === faq.id 
                      ? "bg-violet-500 text-white" 
                      : "bg-white/5 text-gray-400"
                  }`}>
                    <faq.icon className="w-5 h-5" />
                  </div>
                  <span className={`flex-1 font-medium transition-colors ${
                    activeFaq === faq.id ? "text-white" : "text-gray-300"
                  }`}>
                    {faq.question}
                  </span>
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                    activeFaq === faq.id ? "rotate-180 text-violet-400" : "text-gray-500"
                  }`} />
                </button>
              ))}
            </div>

            {/* Visual Answer Panel */}
            <div className="lg:col-span-3">
              <div className="bg-[#231f30] rounded-2xl border border-white/10 overflow-hidden min-h-[400px]">
                {/* Default state */}
                {activeFaq === null && (
                  <div className="p-8 h-full flex flex-col items-center justify-center text-center">
                    <div className="w-20 h-20 bg-violet-500/10 rounded-2xl flex items-center justify-center mb-6 animate-pulse">
                      <HelpCircle className="w-10 h-10 text-violet-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Selecciona una pregunta
                    </h3>
                    <p className="text-gray-400">
                      Haz clic en cualquier pregunta para ver la respuesta visual
                    </p>
                  </div>
                )}

                {/* FAQ 1: Instalación */}
                {activeFaq === 1 && (
                  <div className="p-6 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center">
                        <Wrench className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Instalación DIY</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6">
                      <span className="text-emerald-400 font-semibold">¡No!</span> Plazafy Gate está diseñado para que cualquier persona pueda instalarlo sin conocimientos técnicos.
                    </p>
                    
                    {/* Visual steps */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-4 bg-[#15121e] rounded-xl border border-white/5">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 font-bold">1</div>
                        <div className="flex-1">
                          <p className="text-white font-medium">Conecta el dispositivo</p>
                          <p className="text-gray-400 text-sm">Usa los cables incluidos para conectar al motor</p>
                        </div>
                        <Plug className="w-6 h-6 text-emerald-400" />
                      </div>
                      
                      <div className="flex items-center gap-4 p-4 bg-[#15121e] rounded-xl border border-white/5">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 font-bold">2</div>
                        <div className="flex-1">
                          <p className="text-white font-medium">Descarga la app</p>
                          <p className="text-gray-400 text-sm">Escanea el QR y sigue el asistente</p>
                        </div>
                        <Smartphone className="w-6 h-6 text-emerald-400" />
                      </div>
                      
                      <div className="flex items-center gap-4 p-4 bg-[#15121e] rounded-xl border border-white/5">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 font-bold">3</div>
                        <div className="flex-1">
                          <p className="text-white font-medium">¡Listo para usar!</p>
                          <p className="text-gray-400 text-sm">En menos de 10 minutos</p>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-violet-500/10 rounded-xl border border-violet-500/20">
                      <p className="text-sm text-gray-300">
                        <span className="text-violet-400 font-medium">💡 Incluimos:</span> Videotutorial paso a paso y soporte por chat en tiempo real durante la instalación
                      </p>
                    </div>
                  </div>
                )}

                {/* FAQ 2: Compatibilidad */}
                {activeFaq === 2 && (
                  <div className="p-6 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Compatible con todo</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6">
                      Plazafy Gate funciona con <span className="text-emerald-400 font-semibold">el 99% de los motores</span> del mercado español y europeo.
                    </p>
                    
                    {/* Compatibility grid */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      {["Seccionales", "Basculantes", "Enrollables", "Batientes", "Correderas", "Comunitarias"].map((type) => (
                        <div key={type} className="flex items-center gap-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                          <span className="text-white text-sm">{type}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Brands */}
                    <div className="p-4 bg-[#15121e] rounded-xl border border-white/5">
                      <p className="text-gray-400 text-sm mb-3">Marcas compatibles:</p>
                      <div className="flex flex-wrap gap-2">
                        {["Hörmann", "Nice", "BFT", "FAAC", "Somfy", "Marantec", "Sommer", "Otros"].map((brand) => (
                          <span key={brand} className="px-3 py-1 bg-white/5 text-gray-300 text-xs rounded-full">
                            {brand}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                      <p className="text-sm text-gray-300">
                        <span className="text-emerald-400 font-medium">¿No estás seguro?</span> Envíanos una foto de tu motor y te confirmamos la compatibilidad en 24h
                      </p>
                    </div>
                  </div>
                )}

                {/* FAQ 3: Sin luz/WiFi */}
                {activeFaq === 3 && (
                  <div className="p-6 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Sistema de respaldo</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6">
                      Hemos pensado en todo. Plazafy Gate tiene <span className="text-orange-400 font-semibold">doble sistema de respaldo</span>.
                    </p>
                    
                    {/* Backup systems */}
                    <div className="space-y-4 mb-6">
                      <div className="p-4 bg-[#15121e] rounded-xl border border-orange-500/20">
                        <div className="flex items-center gap-3 mb-2">
                          <Zap className="w-6 h-6 text-orange-400" />
                          <h4 className="text-white font-medium">Si se va la luz</h4>
                        </div>
                        <p className="text-gray-400 text-sm">
                          Batería interna de respaldo que mantiene el dispositivo operativo hasta 8 horas
                        </p>
                        <div className="mt-3 w-full bg-white/10 rounded-full h-2">
                          <div className="bg-orange-500 h-2 rounded-full" style={{width: '100%'}}></div>
                        </div>
                        <p className="text-orange-400 text-xs mt-1">8 horas de autonomía</p>
                      </div>
                      
                      <div className="p-4 bg-[#15121e] rounded-xl border border-blue-500/20">
                        <div className="flex items-center gap-3 mb-2">
                          <Wifi className="w-6 h-6 text-blue-400" />
                          <h4 className="text-white font-medium">Si se va el WiFi</h4>
                        </div>
                        <p className="text-gray-400 text-sm">
                          Tarjeta 4G integrada que toma el relevo automáticamente cuando no hay WiFi
                        </p>
                        <div className="mt-3 flex gap-2">
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">4G LTE</span>
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">Auto-switch</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                      <p className="text-sm text-gray-300">
                        <span className="text-emerald-400 font-medium">✓ Tranquilidad total:</span> Tus inquilinos siempre podrán acceder, sin importar las circunstancias
                      </p>
                    </div>
                  </div>
                )}

                {/* FAQ 4: Seguridad */}
                {activeFaq === 4 && (
                  <div className="p-6 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Seguridad militar</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6">
                      Utilizamos el <span className="text-blue-400 font-semibold">mismo nivel de encriptación que los bancos</span>. Tu garaje está más seguro con Plazafy Gate que sin él.
                    </p>
                    
                    {/* Security layers */}
                    <div className="relative mb-6">
                      <div className="space-y-3">
                        <div className="p-4 bg-blue-500/5 rounded-xl border border-blue-500/20 relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                          <div className="flex items-center gap-3 pl-2">
                            <Lock className="w-5 h-5 text-blue-400" />
                            <div>
                              <p className="text-white font-medium text-sm">Encriptación AES-256</p>
                              <p className="text-gray-500 text-xs">Estándar militar</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-4 bg-blue-500/5 rounded-xl border border-blue-500/20 relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                          <div className="flex items-center gap-3 pl-2">
                            <Key className="w-5 h-5 text-blue-400" />
                            <div>
                              <p className="text-white font-medium text-sm">Tokens de acceso únicos</p>
                              <p className="text-gray-500 text-xs">Caducan automáticamente</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-4 bg-blue-500/5 rounded-xl border border-blue-500/20 relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                          <div className="flex items-center gap-3 pl-2">
                            <Bell className="w-5 h-5 text-blue-400" />
                            <div>
                              <p className="text-white font-medium text-sm">Alertas en tiempo real</p>
                              <p className="text-gray-500 text-xs">Notificación de cada acceso</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
                      <p className="text-sm text-gray-300">
                        <span className="text-blue-400 font-medium">🔒 Auditoría completa:</span> Historial de todos los accesos con fecha, hora y usuario
                      </p>
                    </div>
                  </div>
                )}

                {/* FAQ 5: Precio */}
                {activeFaq === 5 && (
                  <div className="p-6 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center">
                        <Euro className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Inversión que se paga sola</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6">
                      Con una sola reserva mensual, <span className="text-violet-400 font-semibold">Plazafy Gate ya es rentable</span>.
                    </p>
                    
                    {/* Pricing breakdown */}
                    <div className="bg-[#15121e] rounded-xl p-5 border border-white/5 mb-6">
                      <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                        <span className="text-gray-400">Dispositivo (pago único)</span>
                        <span className="text-white font-bold text-xl">89€</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Incluye</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span>Dispositivo Plazafy Gate</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span>Envío gratuito</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span>Kit de instalación</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span>Soporte técnico ilimitado</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span>4G incluido 12 meses</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gradient-to-r from-violet-500/10 to-emerald-500/10 rounded-xl border border-violet-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                          <span className="text-emerald-400 text-lg">💰</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">ROI en 1-2 reservas</p>
                          <p className="text-gray-400 text-sm">Una plaza media genera 50-100€/mes extra con Gate</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* FAQ 6: Soporte */}
                {activeFaq === 6 && (
                  <div className="p-6 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">Soporte premium</h3>
                    </div>
                    
                    <p className="text-gray-300 mb-6">
                      No te dejamos solo. <span className="text-emerald-400 font-semibold">Estamos contigo desde el día 1</span> y para siempre.
                    </p>
                    
                    {/* Support channels */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="p-4 bg-[#15121e] rounded-xl border border-white/5 text-center">
                        <MessageCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-white font-medium text-sm">Chat en vivo</p>
                        <p className="text-gray-500 text-xs">Respuesta en minutos</p>
                      </div>
                      <div className="p-4 bg-[#15121e] rounded-xl border border-white/5 text-center">
                        <Smartphone className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-white font-medium text-sm">WhatsApp</p>
                        <p className="text-gray-500 text-xs">Soporte directo</p>
                      </div>
                      <div className="p-4 bg-[#15121e] rounded-xl border border-white/5 text-center">
                        <HelpCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-white font-medium text-sm">Centro de ayuda</p>
                        <p className="text-gray-500 text-xs">Guías y tutoriales</p>
                      </div>
                      <div className="p-4 bg-[#15121e] rounded-xl border border-white/5 text-center">
                        <Clock className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-white font-medium text-sm">24/7</p>
                        <p className="text-gray-500 text-xs">Emergencias</p>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                      <p className="text-sm text-gray-300">
                        <span className="text-emerald-400 font-medium">📞 Garantía:</span> Si no consigues instalarlo, te enviamos a un técnico sin coste adicional
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Form */}
      <section id="waitlist" className="py-24 bg-gradient-to-b from-[#1a1625] to-[#0f0d15]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Únete a la lista de espera
            </h2>
            <p className="text-xl text-gray-400">
              Sé de los primeros en tener Plazafy Gate y recibe una oferta especial de lanzamiento
            </p>
          </div>

          {submitted ? (
            <div className="bg-[#231f30] rounded-2xl p-8 border border-emerald-500/30 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">¡Gracias por tu interés!</h3>
              <p className="text-gray-400 mb-6">
                Te hemos añadido a la lista de espera. Te contactaremos pronto con novedades sobre Plazafy Gate.
              </p>
              <Link to="/explore">
                <Button className="bg-violet-500 hover:bg-violet-600 text-white">
                  Explorar plazas disponibles
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#231f30] rounded-2xl p-8 border border-white/5">
              <div className="space-y-5">
                <div>
                  <Label className="text-gray-300 mb-2 block">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="gate-email-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 mb-2 block">Nombre</Label>
                    <Input
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 mb-2 block">Teléfono</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+34 600 000 000"
                      className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 mb-2 block">¿Cuántas plazas tienes?</Label>
                    <Input
                      name="num_spots"
                      type="number"
                      min="1"
                      value={formData.num_spots}
                      onChange={handleChange}
                      className="bg-[#15121e] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300 mb-2 block">Ciudad</Label>
                    <Input
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Ej: Madrid"
                      className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 mb-2 block">Comentarios (opcional)</Label>
                  <Textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    placeholder="¿Tienes alguna pregunta o sugerencia?"
                    className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-semibold"
                  data-testid="gate-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Registrando...
                    </>
                  ) : (
                    <>
                      <Bell className="w-5 h-5 mr-2" />
                      Unirme a la lista de espera
                    </>
                  )}
                </Button>

                <p className="text-gray-500 text-sm text-center">
                  No compartiremos tu información con terceros. Recibirás actualizaciones sobre Plazafy Gate.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 mb-4">¿Tienes más preguntas?</p>
          <a href="mailto:info@plazafy.es" className="text-violet-400 hover:text-violet-300 transition-colors">
            Escríbenos a info@plazafy.es
          </a>
        </div>
      </section>
    </div>
  );
}
